<!-- src/components/Inventario/Reportes/ConsumoMateriaPrima/components/CMPGraficaBarras.vue -->
<template>
  <div class="cmp-grafica-wrapper">
    <!-- Título/subtítulo/leyenda viven FUERA del área con scroll horizontal,
         para que no se salgan de vista al desplazar las barras en móvil. -->
    <div class="cmp-grafica-header">
      <h4 class="cmp-grafica-titulo">{{ titulo }}</h4>
      <p v-if="subtitulo" class="cmp-grafica-subtitulo">{{ subtitulo }}</p>
    </div>

    <div class="cmp-grafica-canvas-area" :style="{ height: alturaCanvas }">
      <div class="cmp-grafica-scroll" :style="{ minWidth: anchoMinimo }">
        <canvas v-show="semanas.length" ref="canvasRef"></canvas>
      </div>
      <div v-if="!semanas.length" class="cmp-grafica-empty">
        Sin datos para el periodo seleccionado
      </div>
    </div>

    <div v-if="semanas.length" class="cmp-grafica-leyenda">
      <span class="cmp-grafica-leyenda__item">
        <i class="cmp-grafica-leyenda__marca" :style="{ background: COLOR_PLAN }"></i>
        Plan
      </span>
      <span class="cmp-grafica-leyenda__item">
        <i class="cmp-grafica-leyenda__marca" :style="{ background: COLOR_REAL }"></i>
        Real
      </span>
      <span class="cmp-grafica-leyenda__item">
        <i
          class="cmp-grafica-leyenda__marca cmp-grafica-leyenda__marca--punto"
          :style="{ background: COLOR_PCT }"
        ></i>
        % Cumplimiento
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(...registerables, ChartDataLabels);

const props = defineProps({
  // [{ semana, label, plan, real, porcentaje }]
  semanas: { type: Array, default: () => [] },
  titulo: { type: String, default: "Plan de producción vs producción real" },
  subtitulo: { type: String, default: "" },
});

const COLOR_PLAN = "#3d5a28";
const COLOR_REAL = "#8aa63a";
const COLOR_PCT = "#c0392b";

const alturaCanvas = computed(() => (props.semanas.length > 10 ? "360px" : "320px"));

// ── Responsivo: en pantallas angostas, en vez de exprimir todas las barras
// hasta que se amontonen las etiquetas de %, la gráfica se hace scrolleable
// horizontalmente manteniendo un ancho mínimo legible por barra. Título,
// subtítulo y leyenda se sacaron del canvas a HTML normal (arriba/abajo)
// para que no se vayan con el scroll.
//
// Nota: no se convirtió a barras horizontales — la línea de % Cumplimiento
// comparte un eje dual (y_pct) con las barras, y Chart.js no soporta bien
// mezclar un dataset "line" con orientación horizontal en un eje dual.
const esMovil = ref(false);
let mql = null;

function actualizarEsMovil(e) {
  esMovil.value = e.matches;
  destroyChart();
  nextTick(renderChart);
}

const ANCHO_POR_BARRA_MOVIL = 46; // px por semana, suficiente para que no se amontonen las etiquetas
const ANCHO_MINIMO_MOVIL = 320;

const anchoMinimo = computed(() => {
  if (!esMovil.value) return "100%";
  const ancho = Math.max(
    ANCHO_MINIMO_MOVIL,
    props.semanas.length * ANCHO_POR_BARRA_MOVIL
  );
  return `${ancho}px`;
});

function formatearPiezas(valor) {
  if (!valor && valor !== 0) return "";
  return new Intl.NumberFormat("es-MX").format(valor);
}

const canvasRef = ref(null);
let chartInstance = null;

function buildConfig() {
  const labels = props.semanas.map((s) => s.label);

  return {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          type: "bar",
          label: "Plan",
          data: props.semanas.map((s) => s.plan),
          backgroundColor: COLOR_PLAN,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar",
          label: "Real",
          data: props.semanas.map((s) => s.real),
          backgroundColor: COLOR_REAL,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "line",
          label: "% Cumplimiento",
          data: props.semanas.map((s) => s.porcentaje),
          borderColor: COLOR_PCT,
          backgroundColor: "transparent",
          pointBackgroundColor: COLOR_PCT,
          borderWidth: 2,
          pointRadius: 4,
          tension: 0.3,
          yAxisID: "y_pct",
          order: 1,
          datalabels: {
            // En móvil las cajas de % se amontonan con tan poco espacio por
            // barra — se ocultan ahí; el tooltip al tocar sigue mostrando el dato.
            display: !esMovil.value,
            color: "#c0392b",
            backgroundColor: "rgba(255,255,255,0.85)",
            borderRadius: 4,
            borderColor: "#c0392b",
            borderWidth: 1,
            padding: { top: 3, bottom: 3, left: 5, right: 5 },
            anchor: "end",
            align: "top",
            offset: 4,
            font: { weight: "700", size: 10 },
            formatter: (value) => `${value}%`,
          },
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        // Título, subtítulo y leyenda ahora son HTML fuera del canvas (ver
        // template) — se desactivan aquí para no duplicarlos.
        title: { display: false },
        subtitle: { display: false },
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              if (ctx.dataset.label === "% Cumplimiento") {
                return `% Cumplimiento: ${ctx.parsed.y}%`;
              }
              return `${ctx.dataset.label}: ${formatearPiezas(ctx.parsed.y)} pzas`;
            },
          },
        },
        datalabels: {
          display: (ctx) => ctx.datasetIndex < 2 && (ctx.parsed?.y ?? 0) > 0,
          color: "#fff",
          anchor: "center",
          align: "center",
          font: { weight: "bold", size: 10 },
          formatter: (value) => formatearPiezas(value),
        },
      },
      scales: {
        x: { grid: { display: false } },
        y: {
          beginAtZero: true,
          position: "left",
          title: { display: true, text: "Piezas" },
          ticks: {
            callback: (v) =>
              new Intl.NumberFormat("es-MX", { notation: "compact" }).format(v),
          },
        },
        y_pct: {
          beginAtZero: true,
          position: "right",
          title: { display: true, text: "%" },
          grid: { drawOnChartArea: false },
          ticks: { callback: (v) => `${v}%` },
        },
      },
    },
  };
}

function destroyChart() {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
}

function renderChart() {
  destroyChart();
  if (!canvasRef.value || !props.semanas.length) return;
  chartInstance = new Chart(canvasRef.value, buildConfig());
}

onMounted(() => {
  mql = window.matchMedia("(max-width: 575px)");
  esMovil.value = mql.matches;
  mql.addEventListener("change", actualizarEsMovil);
  nextTick(renderChart);
});
onBeforeUnmount(() => {
  destroyChart();
  if (mql) mql.removeEventListener("change", actualizarEsMovil);
});

watch(
  () => [props.semanas, props.titulo, props.subtitulo],
  () => {
    destroyChart();
    nextTick(renderChart);
  },
  { deep: true }
);
</script>

<style scoped>
.cmp-grafica-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.cmp-grafica-header {
  text-align: center;
}
.cmp-grafica-titulo {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #2c2a24;
}
.cmp-grafica-subtitulo {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: #5a5346;
}

.cmp-grafica-canvas-area {
  position: relative;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.cmp-grafica-scroll {
  height: 100%;
}
.cmp-grafica-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 0.9rem;
}

.cmp-grafica-leyenda {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.25rem;
}
.cmp-grafica-leyenda__item {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #333;
}
.cmp-grafica-leyenda__marca {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}
.cmp-grafica-leyenda__marca--punto {
  border-radius: 50%;
}
</style>
